// Fields chosen from `field.options` ({ title, value }): select, multi_select, radio, checkbox, toggle.
// The value the form keeps is the option object itself ({ title, value }), or a list of them (spec §8); the server
// takes the title from the version again, so only the `value` is trusted.
import { h } from "../h.js";
import { slot_props } from "../styles.js";
import { label_of } from "../messages.js";
import { FieldShell, control_aria } from "./shell.js";

const options_of = (field) => (Array.isArray(field?.options) ? field.options : []);
const as_option = (option) => ({ title: option.title, value: option.value });
const value_of = (item) => (item && typeof item === "object" ? item.value : item);

// ---- select -------------------------------------------------------------------------------------------------------

export const SelectField = ({ field, id, value, on_change, error, required, disabled, styles, class_names, lang, messages }) => {
    const options = options_of(field);
    const control = h(
        "select",
        {
            ...control_aria({ id, field, error, required, disabled }),
            ...slot_props({ slot: "input", class_names, styles, type: field.type }),
            value: value_of(value) ?? "",
            onChange: (event) => {
                const found = options.find((option) => option.value === event.target.value);
                on_change(found ? as_option(found) : undefined);
            },
        },
        h("option", { value: "" }, field.placeholder || label_of("choose", lang, messages)),
        ...options.map((option) => h("option", { key: option.value, value: option.value }, option.title)),
    );
    return h(FieldShell, { field, id, error, required, disabled, styles, class_names }, control);
};

// ---- multi_select: a list box that takes several -----------------------------------------------------------------

export const MultiSelectField = ({ field, id, value, on_change, error, required, disabled, styles, class_names }) => {
    const options = options_of(field);
    const selected = (Array.isArray(value) ? value : []).map(value_of);
    const control = h(
        "select",
        {
            ...control_aria({ id, field, error, required, disabled }),
            ...slot_props({ slot: "input", class_names, styles, type: field.type }),
            multiple: true,
            size: Math.min(Math.max(options.length, 2), 6),
            value: selected,
            onChange: (event) => {
                const chosen = Array.from(event.target.selectedOptions || []).map((item) => item.value);
                const list = options.filter((option) => chosen.includes(option.value)).map(as_option);
                on_change(list.length > 0 ? list : undefined);
            },
        },
        ...options.map((option) => h("option", { key: option.value, value: option.value }, option.title)),
    );
    return h(FieldShell, { field, id, error, required, disabled, styles, class_names }, control);
};

// ---- radio / toggle (one of the options) and checkbox (several) -------------------------------------------------

const choice = ({ field, id, option, index, checked, type_attr, disabled, error, required, styles, class_names, on_pick }) => {
    const option_id = `${id}-${index}`;
    return h(
        "label",
        { key: option.value, htmlFor: option_id, className: [class_names?.choice, checked ? class_names?.is_checked : null].filter(Boolean).join(" ") || undefined, "data-checked": checked ? "true" : undefined },
        h("input", {
            id: option_id,
            name: type_attr === "radio" ? id : `${id}-${index}`,
            type: type_attr,
            value: option.value,
            checked,
            disabled: disabled ? true : undefined,
            "aria-invalid": error ? "true" : undefined,
            "aria-required": required && index === 0 ? "true" : undefined,
            ...slot_props({ slot: "input", class_names, styles, type: field.type }),
            onChange: () => on_pick(option),
        }),
        h("span", null, option.title),
    );
};

export const ChoiceGroupField = ({ field, id, value, on_change, error, required, disabled, styles, class_names }) => {
    const options = options_of(field);
    const multiple = field.type === "checkbox";
    const picked = multiple ? (Array.isArray(value) ? value : []).map(value_of) : [value_of(value)];
    const on_pick = (option) => {
        if (!multiple) {
            on_change(as_option(option));
            return;
        }
        const next = picked.includes(option.value) ? picked.filter((item) => item !== option.value) : [...picked, option.value];
        const list = options.filter((item) => next.includes(item.value)).map(as_option);
        on_change(list.length > 0 ? list : undefined);
    };
    const items = options.map((option, index) => choice({
        field, id, option, index, checked: picked.includes(option.value), type_attr: multiple ? "checkbox" : "radio",
        disabled, error, required, styles, class_names, on_pick,
    }));
    return h(
        FieldShell,
        { field, id, error, required, disabled, styles, class_names, as_fieldset: true, variant: field.type === "toggle" ? "toggle" : "choices" },
        h("div", { className: class_names?.choices, role: multiple ? "group" : "radiogroup", "aria-labelledby": undefined }, ...items),
    );
};
